import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ConfigService } from "src/app/configurations/services/config.service";
import { ManageStaffService } from "src/app/hr-management/services/manage-staff.service";
import { environment } from "src/environments/environment";
import { Subject, Observable } from 'rxjs';
import * as socketIo from 'socket.io-client';

@Injectable({
  providedIn: "root",
})
export class AuthService {
  constructor(
    private manageStaffService: ManageStaffService,
    private configService: ConfigService,
    private http: HttpClient,
  ) {}

  private socket = new Subject<any>();
  
  private loginStatus = new Subject();
  loginStatus$ = this.loginStatus.asObservable();

  private pendingAppts = new Subject();
  pendingAppts$ = this.pendingAppts.asObservable();

  private newReferral = new Subject();
  newReferral$ = this.newReferral.asObservable();

  private newPatient = new Subject();
  newPatient$ = this.newPatient.asObservable();

  private membershipApproved = new Subject();
  membershipApproved$ = this.membershipApproved.asObservable();

  newReferrals() {
    this.newReferral.next(true);
  }

  pendingAppointments() {
    this.pendingAppts.next(true);
  }

  newPatients() {
    this.newPatient.next(true);
  }

  membersApproved() {
    this.membershipApproved.next(true);
  }

  loginStatusChanges() {
    this.loginStatus.next(true);
  }

  isLoggedIn() {
    if (localStorage.getItem("isNonEncDataExists") != "1") {
      localStorage.clear()
      localStorage.setItem("isNonEncDataExists", "1");
    }
    const user = localStorage.getItem("loginDetails")
      ? JSON.parse(this.configService.dec(localStorage.getItem("loginDetails")))
      : [];
    return user && user.Status === "Valid Login";
  }

  getUserInfo() {
    return localStorage.getItem("loginDetails")
      ? JSON.parse(this.configService.dec(localStorage.getItem("loginDetails")))
      : [];
  }

  get staffName() {
    return this.getUserInfo().StaffName;
  }

  get roleName() {
    return this.getUserInfo().Designation;
  }

  get staffImage() {
    return `${environment.configUrl}${this.getUserInfo().ImageName}`;
  }

  get staffId() {
    return this.getUserInfo().StaffID;
  }

  get CanAccessSmileOrchard() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      environment.clinicName
    );
  }

  get CanAccessMyAppointments() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "My Appointments"
    );
  }

  get CanAccessMyTasks() {
    return this.manageStaffService.getStoredStaffRolePermissions("My Tasks");
  }

  get CanAccessHRManagement() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "HR Management"
    );
  }

  get CanAccessPatients() {
    return this.manageStaffService.getStoredStaffRolePermissions("Patients");
  }

  get CanAccessAppointments() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Appointments"
    );
  }

  get CanAccessConfiguration() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Configuration"
    );
  }

  get CanAccessReferrals() {
    return this.manageStaffService.getStoredStaffRolePermissions("Referrals");
  }

  get CanAccessMembership() {
    return this.manageStaffService.getStoredStaffRolePermissions("Membership");
  }

  get CanAccessMembershipTab() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Membership Tab"
    );
  }

  get CanAccessLeaveApplication() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Leave Application"
    );
  }

  get CanAccessHR() {
    return this.manageStaffService.getStoredStaffRolePermissions("HR");
  }

  get CanAccessCompliance() {
    return this.manageStaffService.getStoredStaffRolePermissions("Compliance");
  }

  get CanAccessMyHRCompliance() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "My HR Compliance"
    );
  }

  get CanTransferAppointments() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Transfer Appointments"
    );
  }

  get CanViewMembershipTab() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Membership Tab"
    );
  }

  get CanEditMembershipTab() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Edit Membership Tab"
    );
  }

  get CanEditLeaveApplication() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Edit Leave Application"
    );
  }

  get CanViewLeaveApplication() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Leave Application"
    );
  }

  get CanViewHR() {
    return this.manageStaffService.getStoredStaffRolePermissions("View HR");
  }

  get CanEditHR() {
    return this.manageStaffService.getStoredStaffRolePermissions("Edit HR");
  }

  get CanViewCompliance() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Compliance"
    );
  }

  get CanEditCompliance() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Edit Compliance"
    );
  }

  get CanViewHRCompliance() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View HR Compliance"
    );
  }

  get CanEditHRCompliance() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Edit HR Compliance"
    );
  }

  get CanApprove() {
    return this.manageStaffService.getStoredStaffRolePermissions("Approve");
  }

  get CanCompleteMembership() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Complete Membership"
    );
  }

  get CanChangeMembershipPlan() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Change Membership Plan"
    );
  }

  get CanAddMembership() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Add Membership"
    );
  }

  get CanAddPatientMembership() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Add Patient Membership"
    );
  }

  get CanEditPatients() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Edit Patients"
    );
  }

  get CanAddPatients() {
    return this.manageStaffService.getStoredStaffRolePermissions("Add Patient");
  }

  get CanImportPatientsDetails() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Import Patients Details"
    );
  }

  get CanEditFamilyMembers() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Edit Family members"
    );
  }

  get CanAddNewTimeSlots() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Add New Time Slots"
    );
  }

  get CanBulkTransferAppointments() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Bulk Transfer Appointments"
    );
  }

  get CanAccessPatientFileConfiguration() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Patient File Configuration"
    );
  }

  get CanAccessStaff() {
    return this.manageStaffService.getStoredStaffRolePermissions("Staff");
  }

  get CanAccessPermissions() {
    return this.manageStaffService.getStoredStaffRolePermissions("Permissions");
  }

  get CanAccessRoles() {
    return this.manageStaffService.getStoredStaffRolePermissions("Roles");
  }

  get CanAddNewStaffMember() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Add New Staff Member"
    );
  }

  get CanEditReferrals() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Edit Referrals Tab"
    );
  }

  get CanBookAnAppointment() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Book an Appointment"
    );
  }

  get CanCancelAppointment() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Cancel Appointment"
    );
  }

  get CanEditAppointment() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Edit Appointment"
    );
  }

  get CanMakeDoctorUnAvailable() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Make Doctor unavailable"
    );
  }

  get CanViewAppointments() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Appointments"
    );
  }

  get CanViewMembership() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Membership"
    );
  }

  get CanViewStaff() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View All Staff"
    );
  }

  get CanViewPatients() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Patients"
    );
  }

  get CanViewPermissions() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Permissions"
    );
  }

  get CanViewRoles() {
    return this.manageStaffService.getStoredStaffRolePermissions("View Roles");
  }

  get CanAddTimeSlots() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Add New Time Slots"
    );
  }

  get CanViewTimeSlots() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Time Slots"
    );
  }

  get CanTransferBulkAppointents() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Bulk Transfer Appointments"
    );
  }

  get CanConfigurePatientFile() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Patient File Configuration"
    );
  }

  get CanComposeEmail() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Compose Emails"
    );
  }

  get CanCreateEmailTemplate() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Create Email Templates"
    );
  }

  get CanEditEmailTemplate() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Edit Email Templates"
    );
  }

  get CanViewEmailTemplate() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Email Templates"
    );
  }

  get CanViewSentEmails() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Sent Emails"
    );
  }

  get CanViewReferralsTab() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Referrals Tab"
    );
  }

  get CanViewReferrals() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Referrals"
    );
  }

  get CanSetPermissions() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Set Permissions"
    );
  }

  get CanAddOrUpdateRoles() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Add or Update Roles"
    );
  }

  get CanAccessReferralsMenu() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Referrals Menu"
    );
  }

  get CanAssignReferralStaff() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Assign Referral Staff Tab"
    );
  }

  get CanChangeReferralStatus() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Change Referral Status Tab"
    );
  }

  get CanUploadReferralDocuments() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Upload Referral Documents Tab"
    );
  }

  get CanAddReferralNotes() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Add Referral Notes Tab"
    );
  }

  get CanAccessEmails() {
    return this.manageStaffService.getStoredStaffRolePermissions("Emails");
  }

  get CanViewReferralMenu() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Referral Menu Tab"
    );
  }

  get CanAccessStaffSchedule() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Staff Schedule"
    );
  }

  get CanAccessStaffRota() {
    return this.manageStaffService.getStoredStaffRolePermissions("Staff Rota");
  }

  get CanConfigureSchedule() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Configure Schedule"
    );
  }

  get CanCopySchedule() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Copy Schedule"
    );
  }

  get CanMarkPresentForStaff() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Mark Present For Staff"
    );
  }

  get CanMarkAbsentForStaff() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Mark Absent For Staff"
    );
  }

  get CanModifySchedule() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Modify Schedule"
    );
  }

  // CQC
  get CanAccessCQCTemplates() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "CQC Template"
    );
  }

  get CanViewCQCTemplates() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View CQC Template"
    );
  }

  get CanAccessCQC() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "CQC Compliance"
    );
  }

  get CanAccessOurPolicies() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Our Policies"
    );
  }

  get CanViewOurPolicies() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Our Policies"
    );
  }

  get CanEditOurPolicies() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Edit Our Policies"
    );
  }

  get CanAccessCQCUpcomingReviews() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Upcoming Policy Reviews"
    );
  }

  get CanViewCQCUpcomingReviews() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Policy Reviews"
    );
  }

  get CanEditCQCUpcomingReviews() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Edit Policy Reviews"
    );
  }

  // staff rota
  get CanConfigureRota() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Configure Schedule"
    );
  }

  get CanCopyRota() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Copy Schedule"
    );
  }

  get CanMarkPresentForStaffRota() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Mark Present For Staff"
    );
  }

  get CanMarkAbsentForStaffRota() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Mark Absent For Staff"
    );
  }

  get CanModifyRota() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Modify Schedule"
    );
  }
  get CanAccessMyTaskRota() {
    return this.manageStaffService.getStoredStaffRolePermissions("Staff Rota");
  }

  get CanAccessPracticeConfiguration() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Practice Configuration"
    );
  }

  get CanEditStaffRota() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Add/Edit Staff Rota"
    );
  }

  get CanViewStaffRota() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Staff Rota"
    );
  }

  get CanCopyStaffRota() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Copy Staff Rota"
    );
  }

  get CanViewRotaError() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Rota Errors"
    );
  }

  get CanDeleteStaffError() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Delete Rota Error"
    );
  }

  // practices and referral management
  get CanAccessReferralManagement() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Referral Management"
    );
  }

  get CanAccessPractices() {
    return this.manageStaffService.getStoredStaffRolePermissions("Practices");
  }

  get CanViewPractices() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Practices"
    );
  }

  // inbox and inbox configurations
  get CanAccessInbox() {
    return this.manageStaffService.getStoredStaffRolePermissions("Inbox");
  }

  get CanViewEmailBoxes() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Email Boxes"
    );
  }

  get CanAddFolder() {
    return this.manageStaffService.getStoredStaffRolePermissions("Add Folder");
  }

  get CanDeleteFolder() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Delete Folder"
    );
  }

  get CanViewEmails() {
    return this.manageStaffService.getStoredStaffRolePermissions("View Emails");
  }

  get CanDeleteEmails() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Delete Emails"
    );
  }

  get CanMoveEmails() {
    return this.manageStaffService.getStoredStaffRolePermissions("Move Emails");
  }

  get CanMarkReadOrUnread() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Mark Read/Unread"
    );
  }

  get CanFlagEmail() {
    return this.manageStaffService.getStoredStaffRolePermissions("Flag Email");
  }

  get CanAddLabel() {
    return this.manageStaffService.getStoredStaffRolePermissions("Add Label");
  }

  get CanRemoveLabel() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Remove Label"
    );
  }

  get CanAccessServerConfig() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "IMAP Server Settings"
    );
  }

  get CanInsertOrUpdateServerConfigs() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Insert/Update Imap settings"
    );
  }

  get CanViewServerConfig() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "View Imap Settings"
    );
  }

  get CanDeleteServerConfig() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Delete Imap settings"
    );
  }

  get CanAccessEmailConfig() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Email Settings"
    );
  }

  get CanInsertOrUpdateEmailConfig() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Insert/Update emails"
    );
  }

  get CanViewEmailConfig() {
    return this.manageStaffService.getStoredStaffRolePermissions("View Email");
  }

  get CanDeleteEmailConfig() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Delete Email"
    );
  }

  get CanAddUpdateSignature() {
    return this.manageStaffService.getStoredStaffRolePermissions(
      "Add/Update Signature"
    );
  }

  updateLoginStatusForStaff(StaffId, isLoggedIn, SocketId) {
    return this.http.get<any>(environment.configUrl + `Staff/UpdateLoginStatusForStaff?StaffID=${this.configService.enc(StaffId.toString())}&IsLoggedIn=${isLoggedIn}&SocketID=${SocketId}` + "&CurrentDate=" + new Date());
  }
  
  setSocketConnection(socket: any) {
    this.socket.next({ text: socket });
  }
  getSocketConnection(): Observable<any> {
    return this.socket.asObservable();
  }
}
